"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createFromTdpNode = exports.isTdpTemplate = exports.getTemplateName = void 0;
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const featureFlags_1 = require("../../../common/featureFlags");
const localizeUtils_1 = require("../../../common/localizeUtils");
const utils_1 = require("../../../component/driver/teamsApp/utils/utils");
const templateNames_1 = require("../../../component/generator/templates/templateNames");
const create_1 = require("../../create");
const questionNames_1 = require("../../questionNames");
const agentForTeamsNode_1 = require("./agentForTeamsNode");
const CapabilityOptions_1 = require("./CapabilityOptions");
const createRootNode_1 = require("./createRootNode");
const daProjectTypeNode_1 = require("./daProjectTypeNode");
const ProjectTypeOptions_1 = require("./ProjectTypeOptions");
const teamsProjectTypeNode_1 = require("./teamsProjectTypeNode");
function getTemplateName(inputs) {
    if (inputs.teamsAppFromTdp) {
        const teamsApp = inputs.teamsAppFromTdp;
        // tab with bot, tab with message extension, tab with bot and message extension
        if ((0, utils_1.needTabAndBotCode)(teamsApp)) {
            return templateNames_1.TemplateNames.TabAndDefaultBot;
        }
        // tab only
        if ((0, utils_1.needTabCode)(teamsApp)) {
            return templateNames_1.TemplateNames.Tab;
        }
        // bot and message extension
        if ((0, utils_1.isBotAndBotBasedMessageExtension)(teamsApp)) {
            return templateNames_1.TemplateNames.BotAndMessageExtension;
        }
        // bot based message extension
        if ((0, utils_1.isBotBasedMessageExtension)(teamsApp)) {
            return templateNames_1.TemplateNames.MessageExtension;
        }
        // bot
        if ((0, utils_1.isBot)(teamsApp)) {
            return templateNames_1.TemplateNames.DefaultBot;
        }
    }
    else if (featureFlags_1.featureFlagManager.getBooleanValue(featureFlags_1.FeatureFlags.TdpTemplateCliTest) &&
        inputs.nonInteractive) {
        const capability = inputs[questionNames_1.QuestionNames.Capabilities];
        const map = {
            [CapabilityOptions_1.TdpCapabilityOptions.me().id]: templateNames_1.TemplateNames.MessageExtension,
            [CapabilityOptions_1.TdpCapabilityOptions.nonSsoTab().id]: templateNames_1.TemplateNames.Tab,
            [CapabilityOptions_1.TdpCapabilityOptions.botAndMe().id]: templateNames_1.TemplateNames.BotAndMessageExtension,
            [CapabilityOptions_1.TdpCapabilityOptions.nonSsoTabAndBot().id]: templateNames_1.TemplateNames.TabAndDefaultBot,
        };
        const value = map[capability];
        return value;
    }
}
exports.getTemplateName = getTemplateName;
function isTdpTemplate(inputs) {
    const template = getTemplateName(inputs);
    return template !== undefined;
}
exports.isTdpTemplate = isTdpTemplate;
function createFromTdpNode(platform = teamsfx_api_1.Platform.VSCode) {
    const node = {
        data: { type: "group" },
        children: [
            {
                // templateName is decided by teamsAppFromTdp itself
                condition: (inputs) => getTemplateName(inputs) !== undefined,
                data: {
                    type: "singleSelect",
                    name: questionNames_1.QuestionNames.TemplateName,
                    title: "Select a template",
                    staticOptions: [],
                    dynamicOptions: (inputs) => {
                        const templateName = getTemplateName(inputs);
                        return [templateName];
                    },
                    skipSingleOption: true,
                },
            },
            {
                // templateName can not decided by teamsAppFromTdp itself, need user input
                condition: (inputs) => getTemplateName(inputs) === undefined,
                data: {
                    name: questionNames_1.QuestionNames.ProjectType,
                    title: (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.title"),
                    type: "singleSelect",
                    staticOptions: [
                        ProjectTypeOptions_1.ProjectTypeOptions.declarativeAgent(platform),
                        ProjectTypeOptions_1.ProjectTypeOptions.customEngineAgent(platform),
                        teamsProjectTypeNode_1.TeamsProjectTypeOptions.bot(platform),
                        teamsProjectTypeNode_1.TeamsProjectTypeOptions.tab(platform),
                        teamsProjectTypeNode_1.TeamsProjectTypeOptions.me(platform),
                    ],
                },
                children: [
                    (0, daProjectTypeNode_1.daProjectTypeNode)(),
                    (0, agentForTeamsNode_1.agentForTeamsProjectTypeNode)(),
                    (0, teamsProjectTypeNode_1.botProjectTypeNode)(),
                    (0, teamsProjectTypeNode_1.tabProjectTypeNode)(),
                    (0, teamsProjectTypeNode_1.meProjectTypeNode)(),
                ],
            },
            {
                condition: (inputs) => !!inputs.teamsAppFromTdp && (0, utils_1.isPersonalApp)(inputs.teamsAppFromTdp),
                data: { type: "group", name: questionNames_1.QuestionNames.RepalceTabUrl },
                children: [
                    {
                        condition: (inputs) => {
                            var _a;
                            return (((_a = inputs.teamsAppFromTdp) === null || _a === void 0 ? void 0 : _a.staticTabs.filter((o) => !!o.websiteUrl)) || []).length >
                                0;
                        },
                        data: (0, create_1.selectTabWebsiteUrlQuestion)(),
                    },
                    {
                        condition: (inputs) => {
                            var _a;
                            return !!inputs.teamsAppFromTdp &&
                                (((_a = inputs.teamsAppFromTdp) === null || _a === void 0 ? void 0 : _a.staticTabs.filter((o) => !!o.contentUrl)) || []).length >
                                    0;
                        },
                        data: (0, create_1.selectTabsContentUrlQuestion)(),
                    },
                ],
            },
            {
                condition: (inputs) => !!inputs.teamsAppFromTdp && (0, utils_1.needBotCode)(inputs.teamsAppFromTdp),
                data: (0, create_1.selectBotIdsQuestion)(),
            },
            (0, createRootNode_1.languageNode)(),
            {
                data: (0, create_1.folderQuestion)(),
            },
            {
                data: (0, create_1.appNameQuestion)(),
            },
        ],
    };
    return node;
}
exports.createFromTdpNode = createFromTdpNode;
//# sourceMappingURL=createFromTdpNode.js.map